// src/modules/fire/modals/ModalFireQuoteDetails.tsx

import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Grid,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";

import { globalMaskDocument, maskPhone } from "../../../shared/utils";
import { TokioMarineLogo } from "../icons/TokioMarineLogo";

import {
	StatusChip,
	InfoRow,
	prepareFireQuoteData,
} from "./ModalFireQuoteDetails.helpers";

import { useState } from "react";
import { useFireContract } from "../hooks/useFireContract";
import { ModalFireConfirmContract } from "./ModalFireConfirmContract";

interface ModalBudgetDetailsProps {
	open: boolean;
	onClose: () => void;
	data?: any;
	isLoading?: boolean;
	onClickContract?: () => void;
}

export default function ModalFireQuoteDetails({
	open,
	onClose,
	data,
	isLoading = false,
	onClickContract,
}: ModalBudgetDetailsProps) {
	const theme = useTheme();

	const [confirmOpen, setConfirmOpen] = useState(false);

	const { contract, loading: contractLoading } = useFireContract();

	if (!open) return null;

	/* ---------------- LOADING ---------------- */
	if (!data)
		return (
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth="md"
				fullWidth
				PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
			>
				<DialogContent
					sx={{
						py: 6,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<CircularProgress size={28} />
					<Typography sx={{ ml: 2 }}>Carregando detalhes...</Typography>
				</DialogContent>
			</Dialog>
		);

	/* ---------------- DADOS PRÉ-PROCESSADOS ---------------- */
	const {
		fmt,
		fdate,
		realEstateName,
		totalPrize,
		installments,
		installmentValue,
		endDate,
		coverageLabel,
		addressLine,
	} = prepareFireQuoteData(data);

	/* ---------------- CONFIRMAR CONTRATAÇÃO (interno) ---------------- */
	const handleConfirmContract = () => {
		contract(data.id, () => {
			setConfirmOpen(false);
			onClose();
		});
	};

	/* ---------------- BOTÃO "CONTRATAR" ---------------- */
	const handleClickContractButton = () => {
		if (onClickContract) {
			onClickContract();
			return;
		}
		setConfirmOpen(true);
	};

	return (
		<>
			{/* ===================== MODAL PRINCIPAL ===================== */}
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth="md"
				fullWidth
				PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
			>
				{/* HEADER */}
				<DialogTitle
					sx={{
						px: { xs: 3, md: 4 },
						py: { xs: 3, md: 3.5 },
						background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
						color: theme.palette.primary.contrastText,
						position: "relative",
					}}
				>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Stack direction="row" spacing={2} alignItems="center">
							<Typography variant="h5" fontWeight={500}>
								Detalhes do Orçamento
							</Typography>
							<StatusChip status={data?.status} />
						</Stack>

						<Box sx={{ width: 140, "& svg": { maxHeight: 28, width: "auto" } }}>
							<TokioMarineLogo />
						</Box>
					</Stack>

					<IconButton
						onClick={onClose}
						sx={{
							position: "absolute",
							right: 16,
							top: 16,
							color: "inherit",
							bgcolor: "rgba(255,255,255,0.16)",
							"&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
						}}
						size="small"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				{/* BODY */}
				<DialogContent
					dividers
					sx={{
						px: { xs: 3, md: 4 },
						py: 2,
						bgcolor: "background.default",
					}}
				>
					<Stack spacing={2}>
						{/* CARD 1 — Dados */}
						<Card variant="outlined" sx={{ borderRadius: 3 }}>
							<CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									Dados do orçamento
								</Typography>

								<Grid container spacing={3}>
									<Grid item xs={12} sm={8}>
										<InfoRow
											icon={<BusinessOutlinedIcon fontSize="small" />}
											label="Imobiliária"
											value={realEstateName}
										/>
									</Grid>

									<Grid item xs={12} sm={4}>
										<Box display="flex" justifyContent="flex-end">
											<TokioMarineLogo />
										</Box>
									</Grid>
								</Grid>

								<Grid container spacing={3} sx={{ mt: 0.5 }}>
									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<HomeWorkOutlinedIcon fontSize="small" />}
											label="Tipo de imóvel"
											value={data?.occupation?.riskGroup?.description}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<PeopleAltOutlinedIcon fontSize="small" />}
											label="Ocupação"
											value={data?.occupation?.name}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<SupportAgentOutlinedIcon fontSize="small" />}
											label="Assistência"
											value={data?.assistance?.description ?? "Sem assistência"}
										/>
									</Grid>
								</Grid>

								<Grid container spacing={3} sx={{ mt: 0.5 }}>
									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<EventAvailableOutlinedIcon fontSize="small" />}
											label="Início vigência"
											value={fdate(data?.validity_start)}
										/>
									</Grid>

									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<EventAvailableOutlinedIcon fontSize="small" />}
											label="Fim vigência"
											value={endDate ? fdate(endDate.toISOString()) : "—"}
										/>
									</Grid>

									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<DescriptionOutlinedIcon fontSize="small" />}
											label="Cobertura"
											value={coverageLabel}
										/>
									</Grid>
								</Grid>

								<Grid container spacing={3} sx={{ mt: 0.5 }}>
									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<PaidOutlinedIcon fontSize="small" />}
											label="Valor do aluguel"
											value={fmt(data?.rent_value)}
										/>
									</Grid>

									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<PaidOutlinedIcon fontSize="small" />}
											label="Prêmio total"
											value={fmt(totalPrize)}
										/>
									</Grid>

									<Grid item xs={12} sm={6} md={4}>
										<InfoRow
											icon={<EventAvailableOutlinedIcon fontSize="small" />}
											label="Parcelamento"
											value={`${installments}x de ${fmt(installmentValue)}`}
										/>
									</Grid>
								</Grid>
							</CardContent>
						</Card>

						{/* CARD 2 — Inquilino / Proprietário */}
						<Card variant="outlined" sx={{ borderRadius: 3 }}>
							<CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
								<Stack direction={{ xs: "column", md: "row" }} spacing={3}>
									<Stack flex={1} spacing={1.5}>
										<Typography variant="subtitle2">Inquilino</Typography>

										<InfoRow
											icon={<PersonOutlineOutlinedIcon fontSize="small" />}
											label="Nome"
											value={data?.tenant_name}
										/>
										<InfoRow
											icon={<DescriptionOutlinedIcon fontSize="small" />}
											label="Documento"
											value={globalMaskDocument(data?.tenant_document)}
										/>
										<InfoRow
											icon={<EmailOutlinedIcon fontSize="small" />}
											label="E-mail"
											value={data?.tenant_email}
										/>
										<InfoRow
											icon={<PhoneOutlinedIcon fontSize="small" />}
											label="Telefone"
											value={maskPhone(data?.tenant_mobile_phone)}
										/>
									</Stack>

									<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

									<Stack flex={1} spacing={1.5}>
										<Typography variant="subtitle2">Proprietário</Typography>

										<InfoRow
											icon={<PersonOutlineOutlinedIcon fontSize="small" />}
											label="Nome"
											value={data?.lessor_name}
										/>
										<InfoRow
											icon={<DescriptionOutlinedIcon fontSize="small" />}
											label="Documento"
											value={globalMaskDocument(data?.lessor_document)}
										/>
										<InfoRow
											icon={<EmailOutlinedIcon fontSize="small" />}
											label="E-mail"
											value={data?.lessor_email}
										/>
									</Stack>
								</Stack>
							</CardContent>
						</Card>

						{/* CARD 3 — Endereço */}
						<Card variant="outlined" sx={{ borderRadius: 3 }}>
							<CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									Endereço do risco
								</Typography>

								<InfoRow
									icon={<HomeWorkOutlinedIcon fontSize="small" />}
									label="Endereço completo"
									value={addressLine}
								/>
							</CardContent>
						</Card>

						{/* CARD 4 — Coberturas */}
						<Card variant="outlined" sx={{ borderRadius: 3 }}>
							<CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									Coberturas selecionadas
								</Typography>

								{(!data.coverages || !data.coverages.length) ? (
									<Typography variant="body2">Nenhuma cobertura selecionada.</Typography>
								) : (
									<Box
										sx={{
											backgroundColor: theme.palette.grey[50],
											borderRadius: 2,
											p: 2.5,
										}}
									>
										<Stack spacing={0.8}>
											{data.coverages.map((c: any, idx: number) => (
												<Box
													key={idx}
													display="flex"
													justifyContent="space-between"
													py={0.3}
													sx={{
														borderBottom:
															idx < data.coverages.length - 1
																? "1px dashed rgba(0,0,0,0.1)"
																: "none",
													}}
												>
													<Typography>{c.name ?? c.integrator_code ?? c.coverageId}</Typography>
													<Typography fontWeight={500}>{fmt(c.coverage_value)}</Typography>
												</Box>
											))}
										</Stack>

										<Divider sx={{ my: 2 }} />

										<Box
											sx={{
												display: "flex",
												flexDirection: { xs: "column", sm: "row" },
												justifyContent: "space-between",
											}}
										>
											<Stack>
												<Typography fontWeight={700}>💰 Prêmio total</Typography>
												<Typography fontWeight={600}>{fmt(totalPrize)}</Typography>
											</Stack>

											<Stack>
												<Typography fontWeight={700}>💳 Parcelamento</Typography>
												<Typography fontWeight={600}>
													{`${installments}x de ${fmt(installmentValue)}`}
												</Typography>
												<Typography color="text.secondary">
													Total parcelado: {fmt(totalPrize)}
												</Typography>
											</Stack>
										</Box>
									</Box>
								)}
							</CardContent>
						</Card>
					</Stack>
				</DialogContent>

				{/* FOOTER */}
				<DialogActions
					sx={{
						px: { xs: 3, md: 4 },
						py: 2.5,
						bgcolor: "background.paper",
						justifyContent: "space-between",
					}}
				>
					<Button onClick={onClose}>Fechar</Button>

					{data?.status === "open" && (
						<Button
							variant="contained"
							color="primary"
							disabled={isLoading}
							onClick={handleClickContractButton}
						>
							Contratar
						</Button>
					)}
				</DialogActions>
			</Dialog>

			{/* ===================== MODAL INTERNA DE CONFIRMAÇÃO ===================== */}
			{!onClickContract && (
				<ModalFireConfirmContract
					open={confirmOpen}
					onClose={() => setConfirmOpen(false)}
					data={data}
					loading={contractLoading}
					onConfirm={handleConfirmContract}
				/>
			)}
		</>
	);
}
